import Breadcrumb from "@/components/feature/Breadcrumb";
import ContactForm from "@/pages/about/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  { icon: "ri-newspaper-line", title: "影视资讯", desc: "提供电影、电视剧、综艺的最新动态与深度影评。" },
  { icon: "ri-play-list-2-line", title: "分类浏览", desc: "按地区、年份、类型多维度筛选，快速定位想看的作品。" },
  { icon: "ri-movie-line", title: "作品详情", desc: "详细的影视资料、剧情简介与相关资讯一站式呈现。" },
  { icon: "ri-computer-line", title: "播放演示", desc: "提供在线播放器界面演示，体验流畅的观看交互。" },
];

const notices = [
  { icon: "ri-copyright-line", title: "版权声明", desc: "本站为演示站点，站内影视作品名称、海报、评分与资讯均为虚构演示内容，不涉及任何真实版权作品。若相关名称无意中与真实作品重合，纯属巧合。" },
  { icon: "ri-database-2-line", title: "内容来源说明", desc: "站内所有影视资料、资讯文章均来自站内演示数据，用于展示门户网站的内容组织与浏览体验，不构成任何内容推荐或承诺。" },
  { icon: "ri-play-circle-line", title: "播放功能说明", desc: "播放页仅提供播放器界面演示，暂无真实播放源，不会出现成功播放的状态，请知悉。" },
  { icon: "ri-shield-check-line", title: "个人数据说明", desc: "收藏与浏览记录仅保存在你的浏览器本地，不会上传到服务器。清除浏览器数据后相关内容将被删除。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 17C.COM 影视门户",
    description:
      "了解 17C.COM 的定位、内容范围与服务宗旨。17C.COM 是面向影视爱好者的综合影视门户，提供影视资讯、分类浏览与作品详情展示。",
    keywords: "关于17C.COM,影视门户介绍,联系我们,影视资讯平台",
  });

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-primary-900">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20background%20of%20soft%20dark%20navy%20tones%20with%20subtle%20film%20grain%20and%20bokeh%20light%20spots%2C%20elegant%20minimal%20texture%20suitable%20for%20a%20website%20header%20with%20overlaid%20text%2C%20moody%20premium%20atmosphere&width=1920&height=600&seq=17c-about-001&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 to-primary-900/70" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">
            关于 17C.COM
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            一个为影视爱好者打造的综合影视门户，专注内容呈现与浏览体验。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-12">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

        {/* 网站介绍 */}
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              网站介绍
            </h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              <p>
                17C.COM 是一个面向广大影视爱好者和追剧用户的综合影视门户网站。我们围绕「影视资讯 + 影视作品展示 + 在线播放前端演示」三大方向，为用户提供查询影视动态、获取影评、按分类浏览影视作品以及体验在线播放界面的完整入口。
              </p>
              <p>
                内容范围涵盖最新电影、电视剧与综艺的资讯和影评，以及丰富的影视作品分类浏览与详情展示。我们希望通过清晰的导航和舒适的内容排版，帮助用户更高效地发现值得一看的作品。
              </p>
              <p>
                我们的服务宗旨是：内容真实清晰、界面简洁友好、体验流畅稳定。站内所有影视资料与资讯均为演示数据，播放页仅提供播放器界面演示，我们不对任何真实内容提供播放或下载服务。
              </p>
            </div>
          </div>
          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                我们提供什么
              </h2>
              <ul className="mt-4 space-y-4">
                {services.map((service) => (
                  <li key={service.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${service.icon} text-lg`} aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">
                        {service.title}
                      </h3>
                      <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                        {service.desc}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </section>

        {/* 联系方式 */}
        <section className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              如果你对内容、合作或功能有任何疑问或建议，欢迎通过以下方式联系我们，我们会尽快回复。
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-mail-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs text-foreground-400">邮箱</p>
                  <p className="font-medium text-foreground-800">contact@17c-demo.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-time-line text-lg" aria-hidden="true" />
                </span>
                <div>
                  <p className="text-xs text-foreground-400">回复时间</p>
                  <p className="font-medium text-foreground-800">工作日 9:00 - 18:00</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              在线反馈
            </h2>
            <ContactForm />
          </div>
        </section>

        {/* 使用说明 */}
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            使用说明
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {notices.map((notice) => (
              <div
                key={notice.title}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className={`${notice.icon} text-primary-500`} aria-hidden="true" />
                  {notice.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {notice.desc}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}